<script setup lang="ts">
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Toaster } from 'vue-sonner';
import { PrimeToast } from '#components';

const appHash = ref<string>('');
const frequency = ref<number>(2 * 60 * 1000);
const exceptionErrors = ref<string[]>([]);

const errorsModalRef = ref(null);
const route = useRoute();
const { $events } = useEvents();
const { $alert } = useAlerts();
const { $log } = useLogging();
const { $t } = useLocalization();

onMounted(() =>
{
    $events.$on('errors-modal', toggleErrorsModal);
});

onBeforeMount(async () =>
{
    if (import.meta.env.PROD)
    {
        await checkVersion(`${location.origin}/_nuxt/builds/latest.json`);
    }

    await useThemeStore().initColors();
});

onBeforeUnmount(() =>
{
    $events.$off('errors-modal', toggleErrorsModal);
});

async function checkVersion(url: string): Promise<void>
{
    appHash.value = await loadManifest(url);

    setInterval(async () =>
    {
        const result = await loadManifest(url);

        if (result)
        {
            appHash.value = result;
        }
    },
    frequency.value);
}

async function loadManifest(url: string): Promise<string | null>
{
    try
    {
        const time = new Date();

        time.setSeconds(0);
        time.setMilliseconds(0);

        const response = await axios.get(`${url}?t=${time.getTime()}`);

        return response.data.id;
    }
    catch (ex)
    {
        handleException($log, ex, {
            0: (ex: any) => $alert.danger(ex.message)
        });

        return null;
    }
}

function toggleErrorsModal(ex: any): void
{
    const errors = Object.values(ex ?? {}).flat();

    exceptionErrors.value = [...new Set(errors)] as string[];

    if (errors.length > 0 && !['login', 'forgot-password'].includes((route?.name || '') as string))
    {
        errorsModalRef.value?.show();
        $events.$emit('error-occured');
    }
}

function getErrorMessage(error: string): string
{
    return error.startsWith('[[[') ? $t(error) : error;
}

watch(appHash, (value, old) =>
{
    if (value !== old && old)
    {
        const message = $t('[[[Dostępna jest nowa aktualizacja. Proszę zapisać postęp pracy i klinąć przycisk "Aktualizuj". Możesz również zaakceptować aktualizację poprzez odświeżenie okna przeglądarki. Brak aktualizacji może spowodować błędy.]]]');
        const duration = 1000 * 3600 * 24;

        $alert.info(message, {
            dismissible: false,
            important: true,
            duration,
            action: {
                label: $t('[[[Aktualizuj]]]'),
                onClick: () => location.reload()
            }
        });
    }
});
</script>

<template>
    <div v-if="!$auth.ready()" class="h-100 d-flex justify-content-center align-items-center">
        <Loader />
    </div>
    <NuxtLayout v-else>
        <NuxtPage />
    </NuxtLayout>
    <Toaster
        position="top-center"
        rich-colors
    />
    <IdeoModal ref="errorsModalRef" :title="$t('[[[Wystąpiły błędy podczas zapisywania formularza]]]')" hide-footer>
        <div v-for="(error, index) in exceptionErrors" :key="index" class="text-danger" :class="{ 'mb-2': index < exceptionErrors.length - 1 }">
            <i class="fa-solid fa-fw fa-triangle-exclamation" />
            {{ getErrorMessage(error) }}
        </div>

        <div class="text-end">
            <ActionButton
                :text="$t('[[[Zamknij]]]')"
                variant="outline-dark"
                icon="far fa-xmark"
                @click="errorsModalRef.hide()"
            />
        </div>
    </IdeoModal>
    <PrimeToast />
    <PrimeConfirmPopup group="popup">
        <template #message="{ message }">
            <div class="p-2" v-html="message.message" />
        </template>
    </PrimeConfirmPopup>
    <PrimeConfirmDialog group="dialog">
        <template #message="{ message }">
            <div class="p-2" v-html="message.message" />
        </template>
    </PrimeConfirmDialog>
</template>
